<script lang="ts" setup>
import { computed } from 'vue'
import { v4 as uuid } from 'uuid'
import TagsSelector from './TagsSelector.vue'

const props = defineProps<{
  loading: boolean
  config: any
  activeTab: number
  tagsList: Array<{ id: string; isHot: boolean; tagName: string }>
  activeContentTab: number
  contentTabs: string[]
  filteredBooks: any[]
  tabLoading: boolean
  ending: boolean
  fetching: boolean
  booksData: any[]
  infiniteScrollProps: Record<string, any>
  setContentContainer: (el: HTMLElement | null) => void
  gotoBook: (id: string, firstChapterId?: string) => void
}>()

const emit = defineEmits<{
  (event: 'update:active-tab', value: number): void
  (event: 'update:active-content-tab', value: number): void
}>()

const filterContainerStyle = computed(() => ({
  backgroundColor: props.config.theme.filterBgColor,
}))

const getFilterTabStyle = computed(() => {
  return (active: boolean) => ({
    backgroundColor: active ? props.config.theme.filterActiveBgColor : 'transparent',
    color: active ? props.config.theme.filterActiveColor : props.config.theme.filterColor,
  })
})
</script>

<template>
  <div v-if="loading" class="py-120px flex items-center justify-center space-x-4 text-13px text-n-text-3">
    <div class="animate-spin">
      <i-icon-park-outline-loading />
    </div>
    <p>{{ config.texts.loading }}</p>
  </div>
  <div v-else id="tags" class="h-100dvh bg-n-bg-global overflow-scroll relative">
    <BasicNavbar :scale="true" class="color-n-text-2">
      <template #default>
        <div class="absolute inset-0 flex justify-center items-center pointer-events-none">
          <p class="text-20px text-n-text-1">
            {{ config.texts.allTags }}
          </p>
        </div>
      </template>
    </BasicNavbar>

    <TagsSelector
      :active-tab="activeTab"
      :tags-list="tagsList"
      @update:active-tab="(newVal) => emit('update:active-tab', newVal)"
    />

    <div id="tagsContainer" class="h-[calc(100dvh-120px)] px-4 mb-20px mt-40px">
      <div class="relative bg-n-bg-card h-full flex flex-col rounded-8px">
        <div class="top-0 z-9 bg-n-bg-card w-full my-4">
          <div class="relative mx-2 h-8 rounded-18px" :style="filterContainerStyle">
            <div class="absolute inset-0 flex">
              <button
                v-for="(tab, index) in contentTabs"
                :key="index"
                class="flex-1 h-full text-sm font-medium rounded-[18px]"
                :style="getFilterTabStyle(activeContentTab === index)"
                @click="emit('update:active-content-tab', index)"
              >
                {{ tab }}
              </button>
            </div>
          </div>
        </div>

        <div :ref="setContentContainer" v-bind="infiniteScrollProps" class="flex-1 overflow-y-auto">
          <div v-if="tabLoading" class="py-120px flex items-center justify-center space-x-4 text-13px text-n-text-3">
            <div class="animate-spin">
              <i-icon-park-outline-loading />
            </div>
            <p>{{ config.texts.loading }}</p>
          </div>

          <template v-else>
            <div class="space-y-4">
              <div
                v-for="(item, index) in filteredBooks"
                :key="index"
                class="flex items-center space-x-15px px-3"
                @click="gotoBook(item.id, item.firstChapterId)"
              >
                <BizBookCover
                  :id="uuid()"
                  :src="item.contentCoverUrl || ''"
                  :extra="{
                    author: item?.authorInfo?.author,
                    module_name: 'tags',
                    ...item,
                  }"
                  class="w-18 rounded-8px"
                />

                <div class="flex-1">
                  <p class="text-15px font-bold line-clamp-1 text-n-text-1">
                    {{ item.contentName }}
                  </p>
                  <p class="text-13px mt-10px line-clamp-2 text-n-text-2">
                    {{ item.introduce }}
                  </p>

                  <div class="mt-2 flex items-center">
                    <div class="flex flex-wrap mr-2 flex-1 min-w-0">
                      <span
                        class="inline-flex items-center bg-n-bg-tag rounded-14px text-11px px-6px py-3px whitespace-nowrap text-n-text-2"
                      >
                        {{ item.contentClassName }}
                      </span>
                    </div>
                    <div class="flex-shrink-0 ml-auto pl-2">
                      <div class="flex items-center justify-end text-10px text-n-text-3 w-full">
                        <i-icon-park-outline-preview-open class="w-3 h-3 mr-1 flex-shrink-0" />
                        <span class="whitespace-nowrap">{{ item.viewUsers || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="ending && !fetching" class="py-15px text-13px text-n-text-3 text-center">
              {{ config.texts.noMore }}
            </div>
            <div
              v-if="fetching && booksData.length > 0"
              class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3"
            >
              <div class="animate-spin">
                <i-icon-park-outline-loading />
              </div>
              <p>{{ config.texts.loading }}</p>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
